import React, { useCallback, useEffect, useState } from 'react';
import { ISubscriptionPlan } from '../../redux/subscription/types';
import { TransitionProps } from '@mui/material/transitions';
import { Box, Container, Dialog, DialogTitle, Slide, Stack, Typography } from '@mui/material';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import OutlinedCheckMark from '../CustomSVGIcons/OutlinedCheckMark';
import { PaymentCardDropdown, PaymentCardDropdownProps } from './PaymentCardDropdown';
import { IPaymentCard } from '../../types/global/generic.types';
import CustomButton from '../Button/CustomButton';
import { userConfigurationSelector } from '../../selectors/selectors';
import { useSelector } from 'react-redux';

interface IPlanAdditionModal extends Omit<PaymentCardDropdownProps, 'cards'> {
    open: boolean;
    userCards?: IPaymentCard[];
    plan?: ISubscriptionPlan;
    onClose: () => void;
    onProceedClick: (selectedCard?: IPaymentCard) => void;
    onCancelClick: () => void;
    isDowngrade?: boolean;
    isPromptMode?: boolean;
}

const Transition = React.forwardRef(function Transition(
    props: TransitionProps & {
        children: React.ReactElement<any, any>;
    },
    ref: React.Ref<unknown>,
) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const PlanAdditionModal: React.FC<IPlanAdditionModal> = ({
    open,
    userCards,
    onClose,
    plan,
    onProceedClick,
    onCancelClick,
    isPromptMode,
}) => {
    const { themeStyle, device } = useThemeContext();
    const [selectedCard, setSelectedCard] = useState<IPaymentCard>();
    const userSpecificConfiguration = useSelector(userConfigurationSelector);

    /** Set the default card */

    const handleSetDefaultCard = useCallback(() => {
        if (!userCards) return;
        const defaultCard = userCards?.find(card => card._id === userSpecificConfiguration?.default_payment_method);
        if (defaultCard) {
            setSelectedCard(defaultCard);
        } else {
            setSelectedCard(userCards[0]);
        }
    }, [userCards, userSpecificConfiguration?.default_payment_method]);

    useEffect(() => {
        handleSetDefaultCard();
    }, [handleSetDefaultCard]);

    // If in promptMode and plan is free, don't show the modal
    if (isPromptMode && plan && plan.amount === 0) {
        return null;
    }

    return (
        <Dialog
            open={open}
            TransitionComponent={Transition}
            keepMounted
            onClose={onClose}
            PaperProps={{
                style: {
                    backgroundColor: theme[themeStyle].pure_black,
                    border: `0.5px solid ${theme[themeStyle].darkGray60}`,
                    width: device.isMobile ? '90%' : '450px',
                    maxWidth: '100%',
                    overflowX: 'hidden',
                },
            }}
            aria-describedby="plan-addition-dialog-slide-description">
            <DialogTitle
                variant="h6"
                fontWeight="bold"
                style={{
                    color: theme[themeStyle].lightWhite,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                }}>
                {plan?.name} subscription
            </DialogTitle>
            <Container
                sx={{
                    px: 2,
                    width: '100%',
                    maxWidth: '100%',
                }}>
                {plan &&
                    plan.features.map((feature, index) => (
                        <Box
                            display="flex"
                            alignItems="flex-start"
                            key={index}
                            mt={1}
                            sx={{
                                '& > svg': {
                                    marginRight: '8px',
                                    flexShrink: 0,
                                    marginTop: '3px',
                                },
                                width: '100%',
                            }}>
                            <OutlinedCheckMark stroke={theme[themeStyle].highlight} width="20px" height="20px" />
                            <Typography
                                variant="body2"
                                color="textSecondary"
                                sx={{
                                    textAlign: 'left',
                                    color: theme[themeStyle].tag_white,
                                    wordBreak: 'break-word',
                                    overflowWrap: 'break-word',
                                }}>
                                {feature}
                            </Typography>
                        </Box>
                    ))}
            </Container>
            {plan && plan?.amount > 0 && (
                <Stack dir="column" sx={{ width: '100%' }}>
                    <PaymentCardDropdown cards={userCards} selectedCard={selectedCard} onCardSelect={setSelectedCard} />
                </Stack>
            )}
            <Stack
                width={'100%'}
                p={2}
                gap={1}
                alignItems="flex-end"
                justifyContent="flex-end"
                direction={{
                    xs: 'column',
                    sm: 'row',
                }}>
                <CustomButton
                    label="Confirm"
                    onClick={() => onProceedClick(selectedCard)}
                    disabled={!selectedCard && plan && plan.amount > 0}
                    sx={{
                        width: {
                            xs: '100%',
                            sm: 'fit-content',
                        },
                    }}
                />
                <CustomButton
                    buttonProps={{
                        variant: 'text',
                    }}
                    label="Cancel"
                    onClick={onCancelClick}
                    sx={{
                        width: {
                            xs: '100%',
                            sm: 'fit-content',
                        },
                    }}
                />
            </Stack>
        </Dialog>
    );
};

export { PlanAdditionModal };
